import {
  Message,
  MessageContent,
  MessageResponse,
} from "@/components/ai-elements/message"
import {
  Reasoning,
  ReasoningContent,
  ReasoningTrigger,
} from "@/components/ai-elements/reasoning"
import {
  SourceCardRow,
  type SourceItem,
} from "@/components/ai-elements/sources-custom"
import { LogoMark } from "@/components/common/Logo/LogoMark"
import { FilePreviewModalWithPdfViewer } from "@/components/feature/Modals/FilePreviewModal/FilePreviewModalWithPdfViewer"
import { ToolActivityGroup } from "@/features/agent-chat/components/ToolActivityGroup"
import { isSafeInAppRoute } from "@/features/agent-chat/safeRoute"
import { TurnBlock } from "@/features/agent-chat/types"
import type { TranslationKeys } from "@/hooks/useTranslation/useTranslation"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import { AnalyticsEvent, trackEvent } from "@/services/analytics/amplitude"
import { SourceMetadata } from "@/types/assistantChat"
import { ApiCitation } from "@/types/questionnaire/api"
import { Citation } from "@/types/questionnaire/models"
import { CheckIcon, CopyIcon, GlobeIcon } from "lucide-react"
import React, { useState } from "react"
import { useNavigate } from "react-router"
import remarkGfm from "remark-gfm"
import { useTypewriter } from "./hooks/useTypewriter"
import { normalizeCitations } from "./utils/chatUtils"

interface MessageRendererProps {
  role: "user" | "assistant" | "system"
  content: string
  /**
   * The turn as ordered text and collapsed tool groups. When present,
   * rendered instead of `content` — which still holds the flat concatenation
   * of every text chunk, and is what a turn persisted before activity
   * tracking existed falls back to.
   */
  blocks?: TurnBlock[]
  reasoning?: string
  reasoningDuration?: number
  isReasoningStreaming?: boolean
  citations?: Citation[]
  showLoading?: boolean
  sourceMetadata?: SourceMetadata
}

// Localized display names for mc-data citation cards, keyed by the backend
// tool id transported in reference.metadata.tool.
const MC_TOOL_LABEL_KEYS: Record<string, TranslationKeys> = {
  list_article_compliance: "AI_CHAT/MC_TOOLS/LIST_ARTICLE_COMPLIANCE",
  get_article_details: "AI_CHAT/MC_TOOLS/GET_ARTICLE_DETAILS",
  list_supplier_inquiries: "AI_CHAT/MC_TOOLS/LIST_SUPPLIER_INQUIRIES",
  get_mc_overview_stats: "AI_CHAT/MC_TOOLS/GET_MC_OVERVIEW_STATS",
  list_articles_containing_substance:
    "AI_CHAT/MC_TOOLS/LIST_ARTICLES_CONTAINING_SUBSTANCE",
  list_article_substances: "AI_CHAT/MC_TOOLS/LIST_ARTICLE_SUBSTANCES",
  get_regulation_info: "AI_CHAT/MC_TOOLS/GET_REGULATION_INFO",
  list_articles_citing_exemption:
    "AI_CHAT/MC_TOOLS/LIST_ARTICLES_CITING_EXEMPTION",
}

const isValidUrl = (url: string | undefined): url is string => {
  if (!url) return false
  try {
    const parsed = new URL(url)
    return ["http:", "https:", "mailto:"].includes(parsed.protocol)
  } catch {
    return false
  }
}

export const MessageRenderer: React.FC<MessageRendererProps> = ({
  role,
  content,
  blocks,
  reasoning,
  reasoningDuration,
  isReasoningStreaming,
  citations = [],
  showLoading = false,
  sourceMetadata,
}) => {
  const { translate } = useTranslation()
  const navigate = useNavigate()
  const [copied, setCopied] = useState(false)
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false)
  const [selectedCitation, setSelectedCitation] = useState<Citation | null>(
    null,
  )
  const isUser = role === "user"
  const displayedContent = useTypewriter(content, !isUser && showLoading)

  // Helpdesk articles are partitioned out first and rendered in their own group
  // below. They are turnus's own documentation, not the customer's data, and a source
  // pill is only trustworthy if those two can never be read as the same thing.
  const helpCitations = citations.filter(
    (citation) => citation.sourceType === "help",
  )
  const workspaceCitations = citations.filter(
    (citation) => citation.sourceType !== "web" && citation.sourceType !== "help",
  )
  const webCitations = citations.filter(
    (citation) => citation.sourceType === "web",
  )

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      // ignore
    }
  }

  // Help articles live at an in-app route, so this navigates rather than opening a tab
  // or a document preview. The pill used to be inert on the theory that
  // open_help_article owned navigation, which was wrong twice over: that card is only
  // emitted when the agent judges the user should read the whole article, so a complete
  // answer left the article named with no way to reach it; and a confirmation card
  // exists to gate AGENT-initiated navigation, while clicking a source is the user's
  // own choice, exactly as it already is for a document citation.
  const handleHelpSourceClick = (citation: Citation) => {
    const route = citation.url
    // Same guard the action parser applies, shared rather than repeated. Kept here as
    // well as on the onClick gate below: the gate decides whether a control is offered,
    // this decides whether navigation happens, and they must not be able to disagree.
    if (!isSafeInAppRoute(route)) return

    trackEvent(AnalyticsEvent.CHAT_SOURCE_CLICKED, {
      source_type: "help",
    })

    navigate(route)
  }

  const handleSourceClick = (citation: Citation) => {
    if (citation.sourceType === "web" && citation.url) {
      if (isValidUrl(citation.url)) {
        window.open(citation.url, "_blank", "noopener")
      }
      return
    }

    // mc-data citations have no backing document (only a synthetic reference
    // id) — opening the PDF preview would show a broken viewer.
    if (citation.sourceType === "mc-data" || !citation.sourceFileId) {
      return
    }

    trackEvent(AnalyticsEvent.CHAT_SOURCE_CLICKED, {
      source_type: "citation",
    })

    setSelectedCitation(citation)
    setIsPreviewModalOpen(true)
  }

  // One pill per citation, shared by the source list under the answer and the
  // tool rows, so a source opens the same target wherever it is clicked.
  const toSourceItem = (citation: Citation, key: string): SourceItem => {
    if (citation.sourceType === "help") {
      return {
        key,
        title: citation.sourceFileName,
        sourceType: "help",
        snippet: citation.sourceText,
        // Gated on the SAME predicate the handler uses. Testing only for a
        // present url rendered a button for an off-site route that the handler
        // then refused, which is the dead-end control this change exists to
        // remove, just one layer further in.
        onClick: isSafeInAppRoute(citation.url)
          ? () => handleHelpSourceClick(citation)
          : undefined,
      }
    }
    if (citation.sourceType === "web") {
      return {
        key,
        title: citation.sourceFileName,
        sourceType: "web",
        url: citation.url,
        snippet: citation.sourceText,
        onClick: (event) => {
          if (!isValidUrl(citation.url)) {
            event.preventDefault()
            return
          }
          handleSourceClick(citation)
        },
      }
    }
    const isMcData = citation.sourceType === "mc-data"
    return {
      key,
      title: isMcData
        ? translate(
            (citation.toolName && MC_TOOL_LABEL_KEYS[citation.toolName]) ||
              "AI_CHAT/SOURCES/MC_LABEL",
          )
        : citation.sourceFileName,
      subtitle:
        !isMcData && citation.sourceFilePage
          ? translate("AI_CHAT/SOURCE_PAGE", {
              page: citation.sourceFilePage,
            })
          : undefined,
      sourceType: isMcData ? "mc-data" : "rag",
      snippet: citation.sourceText,
      // mc-data has no backing document — keep the badge static
      onClick: isMcData ? undefined : () => handleSourceClick(citation),
    }
  }

  const toolSourceItems = (toolCitations: ApiCitation[]): SourceItem[] =>
    normalizeCitations(toolCitations, translate("AI_CHAT/UNKNOWN_SOURCE")).map(
      (citation, idx) =>
        toSourceItem(
          citation,
          `tool-${citation.sourceFileId}-${citation.sourceFilePage}-${idx}`,
        ),
    )

  return (
    <div className="mb-L space-y-2">
      <div
        className={cn(
          "flex items-start gap-S",
          isUser ? "justify-end" : "justify-start",
        )}
      >
        {isUser ? null : (
          <div
            className={cn(
              "relative mt-1 flex size-9 shrink-0 items-center justify-center rounded-xl text-primary",
              showLoading
                ? "shadow-[0_0_28px_hsl(var(--primary)/0.32)]"
                : "shadow-[0_0_18px_hsl(var(--primary)/0.14)]",
            )}
          >
            <LogoMark className="size-5" />
          </div>
        )}

        <div
          className={cn(
            "group",
            isUser ? "max-w-[min(560px,82%)]" : "min-w-0 flex-1",
          )}
        >
          <Message
            from={role}
            className={
              isUser ? "max-w-full" : "max-w-[min(1040px,88%)] justify-start"
            }
          >
            <MessageContent
              className={cn(
                "relative max-w-full rounded-xl px-M py-S text-[15px] leading-relaxed [&_li]:ml-2 [&_ol]:ml-6 [&_ol]:list-decimal [&_ul]:ml-6 [&_ul]:list-disc",
                isUser
                  ? "bg-muted/60! text-foreground backdrop-blur-xs"
                  : "overflow-visible! bg-grey2/55! shadow-[0_18px_42px_-18px_hsl(var(--background)/0.85)] backdrop-blur-md before:absolute before:bottom-S before:left-0 before:top-S before:w-px before:rounded-full before:bg-linear-to-b before:from-transparent before:via-primary/60 before:to-transparent",
              )}
            >
              {content && (
                <button
                  type="button"
                  onClick={handleCopy}
                  aria-label={translate("AI_CHAT/COPY_MESSAGE")}
                  className="absolute right-2 top-2 z-10 h-7 w-7 rounded-md border border-border/60 bg-grey1/80 p-1.5 text-muted-foreground opacity-0 shadow-xs transition-opacity hover:border-primary/40 hover:bg-primary/10 hover:text-primary group-hover:opacity-100"
                >
                  {copied ? (
                    <CheckIcon className="h-4 w-4 text-success" />
                  ) : (
                    <CopyIcon className="h-4 w-4" />
                  )}
                </button>
              )}
              {role === "assistant" && reasoning !== undefined && (
                <Reasoning
                  isStreaming={isReasoningStreaming}
                  // Collapsed from the start, and `Reasoning` reads the same
                  // prop as its auto-close gate — so this also stops it
                  // opening on its own and shutting again a second later.
                  defaultOpen={false}
                  duration={reasoningDuration}
                  className="mb-S"
                >
                  <ReasoningTrigger
                    className="rounded-md text-xs text-muted-foreground transition-colors hover:text-primary"
                    thinkingLabel={translate("AI_CHAT/REASONING_THINKING")}
                    thoughtLabel={(duration) =>
                      translate("AI_CHAT/REASONING_THOUGHT_FOR").replace(
                        "{duration}",
                        String(duration),
                      )
                    }
                    thoughtBriefLabel={translate(
                      "AI_CHAT/REASONING_THOUGHT_BRIEF",
                    )}
                  />
                  {reasoning && (
                    <ReasoningContent className="mt-2 border-l border-primary/25 px-S py-XS text-xs italic leading-relaxed text-muted-foreground [&_em]:not-italic [&_strong]:not-italic">
                      {reasoning}
                    </ReasoningContent>
                  )}
                </Reasoning>
              )}
              {blocks && blocks.length > 0
                ? blocks.map((block, idx) =>
                    block.kind === "tools" ? (
                      <ToolActivityGroup
                        key={`tools-${idx}`}
                        block={block}
                        toSourceItems={toolSourceItems}
                      />
                    ) : (
                      block.text.trim() && (
                        <MessageResponse
                          key={idx}
                          remarkPlugins={[remarkGfm]}
                          controls={{ table: true }}
                        >
                          {block.text}
                        </MessageResponse>
                      )
                    ),
                  )
                : showLoading &&
                  displayedContent &&
                  (() => {
                    const splitIdx = displayedContent.lastIndexOf("\n\n")
                    const settled =
                      splitIdx >= 0 ? displayedContent.slice(0, splitIdx) : ""
                    const live =
                      splitIdx >= 0
                        ? displayedContent.slice(splitIdx + 2)
                        : displayedContent
                    return (
                      <>
                        {settled && (
                          <MessageResponse
                            remarkPlugins={[remarkGfm]}
                            controls={{ table: true }}
                          >
                            {settled}
                          </MessageResponse>
                        )}
                        {live && (
                          <p className="whitespace-pre-wrap text-[15px] leading-relaxed after:ml-0.5 after:inline-block after:h-[1em] after:w-[2px] after:translate-y-[2px] after:rounded-[1px] after:bg-primary after:align-baseline after:shadow-[0_0_10px_hsl(var(--primary)/0.7)] after:content-['']">
                            {live}
                          </p>
                        )}
                      </>
                    )
                  })()}
              {!blocks?.length && !showLoading && content && (
                <MessageResponse
                  remarkPlugins={[remarkGfm]}
                  controls={{ table: true }}
                  className="[&_table]:block [&_table]:max-w-full [&_table]:overflow-x-auto"
                >
                  {content}
                </MessageResponse>
              )}
            </MessageContent>
          </Message>
        </div>
      </div>

      {!isUser && sourceMetadata?.webSearchApplied && (
        <div
          className={`flex ${isUser ? "justify-end" : "justify-start"} gap-S`}
        >
          <div className="w-10 shrink-0" />
          <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary/90">
            <GlobeIcon className="h-3 w-3" />
            {translate("AI_CHAT/WEB_SEARCH/STATUS_ON")}
          </span>
        </div>
      )}

      {sourceMetadata?.webSearchEnabled &&
        !sourceMetadata?.webSearchApplied &&
        sourceMetadata?.webSearchNotice && (
          <div
            className={`flex ${isUser ? "justify-end" : "justify-start"} gap-2`}
          >
            <div className="w-10 shrink-0" />
            <p
              className={`text-xs italic text-muted-foreground ${isUser ? "text-right" : "text-left"}`}
            >
              {sourceMetadata.webSearchNotice}
            </p>
          </div>
        )}

      {/* Its own row, above the workspace sources and never merged into their items
          array. Interleaving would let "from the helpdesk" and "from your documents"
          sit side by side as if they were the same kind of claim. */}
      {!isUser && helpCitations.length > 0 && (
        <div className="flex flex-row gap-S">
          <div className="w-10 shrink-0" />
          <SourceCardRow
            className="flex-1"
            items={helpCitations.map((citation, idx) =>
              toSourceItem(
                citation,
                `help-${citation.sourceFileId || citation.sourceFileName}-${idx}`,
              ),
            )}
          />
        </div>
      )}

      {!isUser && workspaceCitations.length + webCitations.length > 0 && (
        <div className="flex flex-row gap-S">
          <div className="w-10 shrink-0" />
          <SourceCardRow
            className="flex-1"
            items={[
              ...workspaceCitations.map((citation, idx) =>
                toSourceItem(
                  citation,
                  `ws-${citation.sourceFileId}-${citation.sourceFilePage}-${idx}`,
                ),
              ),
              ...webCitations.map((citation, idx) =>
                toSourceItem(citation, `web-${citation.sourceFileId}-${idx}`),
              ),
            ]}
          />
        </div>
      )}

      {isUser && citations.length > 0 && (
        <div className="flex flex-row-reverse gap-S">
          <div className="w-10 shrink-0" />
          <SourceCardRow
            className="max-w-[min(560px,82%)]"
            align="end"
            items={citations.map<SourceItem>((citation, idx) => ({
              key: `user-${citation.sourceFileId}-${idx}`,
              title: citation.sourceFileName,
              subtitle: citation.sourceFilePage
                ? translate("AI_CHAT/SOURCE_PAGE", {
                    page: citation.sourceFilePage,
                  })
                : undefined,
              sourceType:
                citation.sourceType === "web"
                  ? "web"
                  : citation.sourceType === "mc-data"
                    ? "mc-data"
                    : "rag",
              url: citation.url,
              snippet: citation.sourceText,
              onClick: (event) => {
                if (
                  citation.sourceType === "web" &&
                  !isValidUrl(citation.url)
                ) {
                  event.preventDefault()
                  return
                }
                handleSourceClick(citation)
              },
            }))}
          />
        </div>
      )}

      {isPreviewModalOpen && selectedCitation && (
        <FilePreviewModalWithPdfViewer
          urlQueryId={selectedCitation.sourceFileId}
          citation={selectedCitation}
          setIsPreviewModalOpen={setIsPreviewModalOpen}
          fullScreen={true}
        />
      )}
    </div>
  )
}
